import { TimeOffCategoryPolicySchema } from "@/pages/settings/time-off/policy/TimeOffCategoryPolicyEdit.tsx";
import {
  ApiTimeOffPeriodTrackingTime,
  ApiTimeOffPolicyMilestoneCarryoverType,
  getCarryoverExpiringDateTypes,
  getMilestoneCarryoverTypes,
} from "@/types/time-off/time-off-policy.types.ts";
import { getCarryoverExpiringEnabled } from "@/types/time-off/time-off.types.ts";
import { Box, DropdownSelect, Input, ListItem, Select, Stack, Title } from "@skailer-com/uikit";
import { Controller, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface TimeOffCategoryPolicyCarryoverExpirationProps {
  formHook: UseFormReturn<TimeOffCategoryPolicySchema>;
  periodTrackType?: ApiTimeOffPeriodTrackingTime;
}

export function TimeOffPolicyCarryoverExpiration({
  formHook,
  periodTrackType,
}: TimeOffCategoryPolicyCarryoverExpirationProps) {
  const { t } = useTranslation();

  const {
    watch,
    control,
    register,
    formState: { errors },
  } = formHook;

  const carryoverExpireEnable = watch("carryover_expire_enable");

  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;

  const carryoverExpirationTitle = t("time_off:carryover_expiration", {
    units: isHours
      ? t("time_off:hours_genitive").toLowerCase()
      : t("time_off:days_genitive").toLowerCase(),
  });

  const carryoverType = watch("carryover_type");
  const milestoneCarryoverTypes = getMilestoneCarryoverTypes();

  return (
    <>
      <Stack gap={"sm"}>
        <Title header={t("time_off:carryover")} size={"xs"} paddingBottom />
        <Box variant={"border"}>
          <Stack className={"my-3"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <ListItem
                  layout={"two-columns"}
                  behavior={"card"}
                  title={t("time_off:milestone_carryover_settings_description")}
                  caption={t("time_off:milestone_carryover_settings_caption")}
                  valueSlot={
                    <DropdownSelect
                      value={value}
                      onChange={onChange}
                      options={milestoneCarryoverTypes}
                    />
                  }
                />
              )}
              control={control}
              name={"carryover_type"}
            />
            {carryoverType == ApiTimeOffPolicyMilestoneCarryoverType.up_to && (
              <Box className={"w-60"}>
                <Input
                  type={"number"}
                  {...register("carryover_amount")}
                  error={errors.carryover_amount?.message}
                  label={isHours ? t("time_off:hours") : t("time_off:days")}
                />
              </Box>
            )}
          </Stack>
        </Box>
        {carryoverType !== ApiTimeOffPolicyMilestoneCarryoverType.none && (
          <Box variant={"border"}>
            <Stack className={"my-3"}>
              <Controller
                render={({ field: { value, onChange } }) => {
                  return (
                    <ListItem
                      layout={"two-columns"}
                      behavior={"card"}
                      title={carryoverExpirationTitle}
                      caption={t("time_off:carryover_expiration_caption")}
                      valueSlot={
                        <DropdownSelect
                          value={Number(value)}
                          onChange={(value) => {
                            onChange(Boolean(value));
                          }}
                          options={getCarryoverExpiringEnabled(isHours)}
                        />
                      }
                    />
                  );
                }}
                control={control}
                name={"carryover_expire_enable"}
              />
              {carryoverExpireEnable && (
                <Stack direction={"horizontal"} gap={"md"}>
                  <Input
                    type={"number"}
                    {...register("carryover_expire_date_value.count")}
                    error={errors.carryover_expire_date_value?.count?.message}
                    label={t("common:quantity_short")}
                  />
                  <Box className={"shrink-0 grow"}>
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("time_off:carryover_expiration_date_title")}
                          options={getCarryoverExpiringDateTypes()}
                          emptyTitle={t("common:select")}
                          value={value}
                          onChange={onChange}
                          error={errors.carryover_expire_date_type?.message}
                        />
                      )}
                      control={control}
                      name={"carryover_expire_date_type"}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </>
  );
}
